import { CheckOutlined, CloseOutlined, SaveOutlined } from "@ant-design/icons";
import { Alert, Button, Col, DatePicker, Divider, Drawer, Empty, Flex, Form, Image, Input, InputNumber, notification, Row, Select, Skeleton, Spin, Typography } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useLazyGetCategoriesBreedInByFarmQuery, useLazyGetFarmsForSalesOrderQuery } from "api";
import { useLazyGetPriceListByCustomerCodeAndProDuctCodeAndFarmIdQuery } from "api/priceListApi";
import { useLazyGetCustomerShippingQuery, useLazyGetCustomersQuery } from "api/sales-order/customer-api";
import { useCreateSalesOderV2Mutation, useGetOneSalesOrderV2Query, useGetSalesOrderDetailQuery, useUpdateSalesOderV2Mutation } from "api/sales-order/sales-order-api";
import { OBJ_COLOR } from "commons/constants";
import { StatusSalesOrder } from "commons/constants/sales-order";
import { getUrlImage, handleSelectFilter, onKeyPress } from "commons/constants/util";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import useLoading from "hooks/useLoading";
import useSelect from "hooks/useSelect";
import { useCallback, useEffect, useState } from "react";
import { EnumStatusPriceList, PriceListResponse } from "types/price-list.type";
import AddImage from "./AddImage";

dayjs.extend(utc);
dayjs.extend(timezone);

const FORM_LIST_NAME = "salesOderDetail";

export enum GenderChicken {
	"Trống" = "Trống",
	"Mái" = "Mái",
	"Xô" = "Xô",
}

const COLOR_GENDER = [OBJ_COLOR.INFO, OBJ_COLOR.DANGER, OBJ_COLOR.SUCCESS];

interface FormSalesOrderDetailModal {
	gender: string;
	priceListId: number;
	priceAmount: number;
	currency: string;
	uom: string;
	quantity: number;
}

export interface FormSalesOrderModal {
	custPhone: string;
	customerId: string;
	driver: string;
	driverPhone: string;
	farmCode: string;
	orderDate: dayjs.Dayjs;
	shipDate: dayjs.Dayjs;
	shipAddress: string;
	itemCode: string;
	chickenType: number;
	note?: string;
	salesOderDetail: FormSalesOrderDetailModal[];
}

export interface IBuildSalesOrderDetail {
	priceListCode: string;
	gender: string;
	priceListId: number;
	priceAmount: number;
	currency: string;
	uom: string;
	quantity: number;
}

export const buildSalesOrderDetail = (dataSalesOrderDetail: SalesOrderDetail, priceList: PriceListResponse[], priceListIds: number[]): IBuildSalesOrderDetail[] => {
	const { femaleQuantity, maleQuantity, noneQuantity } = dataSalesOrderDetail;

	const array: IBuildSalesOrderDetail[] = [];

	priceListIds.forEach((id) => {
		const price = priceList.find((item) => item.id === id);

		if (price) {
			let quantity = 0;

			if (price.gender === GenderChicken.Mái) {
				quantity = femaleQuantity;
			} else if (price.gender === GenderChicken.Trống) {
				quantity = maleQuantity;
			} else if (price.gender === GenderChicken.Xô) {
				quantity = noneQuantity;
			}

			array.push({
				priceListCode: price.priceListCode,
				gender: price.gender as string,
				priceListId: price.id,
				priceAmount: price.priceAmount,
				currency: price.currency,
				uom: price.uom,
				quantity,
			});
		}
	});

	return array;
};

interface SalesOrderModalProps {
	open: boolean;
	onClose: () => void;
	salesOrderId: number;
}

const SalesOrderModalV2 = ({ open, onClose, salesOrderId }: SalesOrderModalProps) => {
	const { isLoading: isLoadingQueries } = useLoading("queries");
	const { isLoading: isLoadingMutations } = useLoading("mutations");

	const [form] = Form.useForm<FormSalesOrderModal>();

	const [getCustomers, { data: dataGetCustomers }] = useLazyGetCustomersQuery();
	const { options: optionsCustomers } = useSelect<Customer>(getCustomers, dataGetCustomers);

	const [getFarmsForSalesOrder, { data: dataFarms }] = useLazyGetFarmsForSalesOrderQuery();
	const { options: optionsFarms, handleOnClear: handleOnClearFarms, isFetching: isFetchingGetFarms } = useSelect<FarmsReport>(getFarmsForSalesOrder, dataFarms);

	const [getCustomerShipping, { data: dataCustomerShipping }] = useLazyGetCustomerShippingQuery();

	const [getCategoriesBreedInByFarm, { data: dataBreedInByFarm }] = useLazyGetCategoriesBreedInByFarmQuery();

	const [createSalesOderV2] = useCreateSalesOderV2Mutation();
	const [updateSalesOderV2] = useUpdateSalesOderV2Mutation();

	const [orderDate, setOrderDate] = useState<dayjs.Dayjs>(dayjs().tz("Asia/Ho_Chi_Minh"));

	const [status, setStatus] = useState("");
	const [pathImage, setPathImage] = useState<string[]>([]);

	const { data: dataSalesOrder } = useGetOneSalesOrderV2Query(salesOrderId, { skip: !salesOrderId, refetchOnMountOrArgChange: true });
	const { data: dataSalesOrderDetail } = useGetSalesOrderDetailQuery(salesOrderId, { skip: !salesOrderId, refetchOnMountOrArgChange: true });

	const [getPriceListByCustomerCodeAndProDuctCodeAndFarmId, { data: dataPriceList }] = useLazyGetPriceListByCustomerCodeAndProDuctCodeAndFarmIdQuery();

	useEffect(() => {
		if (!open) {
			form.resetFields();
			return;
		}

		if (!dataSalesOrder || !dataSalesOrderDetail || !salesOrderId) {
			form.resetFields();
			return;
		}

		const run = async () => {
			const { orderDate, shipDate, pathImage, priceListIds } = dataSalesOrder;

			form.setFieldsValue({ ...dataSalesOrder, orderDate: dayjs(orderDate), shipDate: dayjs(shipDate) });

			getCategoriesBreedInByFarm({ farmCode: dataSalesOrder.farmCode });

			setPathImage(pathImage.split("|"));

			if (!dataSalesOrderDetail.length) return;

			const { itemCode, chickenType } = dataSalesOrderDetail[0];

			form.setFieldsValue({ itemCode });

			const priceList = await getPriceListByCustomerCodeAndProDuctCodeAndFarmId({
				productCode: itemCode,
				customerCode: dataSalesOrder.customerId,
				farmId: optionsFarms.find((item) => item.farm_code === dataSalesOrder.farmCode)?.id || 0,
				status: EnumStatusPriceList.valid,
				chickenType,
				validFrom: dataSalesOrder.shipDate,
				validTo: dataSalesOrder.shipDate,
			}).unwrap();

			form.setFieldsValue({ salesOderDetail: buildSalesOrderDetail(dataSalesOrderDetail[0], priceList.items, priceListIds || []) as any });
		};

		run();
	}, [open, dataSalesOrder, dataSalesOrderDetail, form, getCategoriesBreedInByFarm, salesOrderId, getPriceListByCustomerCodeAndProDuctCodeAndFarmId, optionsFarms]);

	useEffect(() => {
		if (!dataCustomerShipping?.length) return;

		form.setFieldValue("shipAddress", dataCustomerShipping[0].cusAddress);
	}, [dataCustomerShipping, form]);

	const handleOnChangeGender = useCallback(() => {
		const salesOderDetail = (form.getFieldValue([FORM_LIST_NAME]) as FormSalesOrderDetailModal[]) || [];

		const gender = (form.getFieldValue("gender") as string[]) || [];

		form.setFieldValue(
			FORM_LIST_NAME,
			gender?.map((str) => {
				const find = dataPriceList?.items.find((price) => price.gender === str);

				const detail = salesOderDetail?.find((i) => i.gender === str);

				return {
					// gender: detail?.gender || str,
					// priceListId: detail?.priceListId || find.id,
					// quantity: detail?.quantity || 0,
					// priceAmount: detail?.priceAmount || find.priceAmount,
					// currency: detail?.currency || find.currency,
					// uom: detail?.uom || find.uom,

					gender: detail?.gender || str,
					priceListId: find?.id || undefined,
					quantity: detail?.quantity || 0,
					priceAmount: find?.priceAmount || 0,
					currency: find?.currency || undefined,
					uom: find?.uom || undefined,
				};
			})
		);
	}, [dataPriceList?.items, form]);

	useEffect(() => {
		if (!dataPriceList?.items) return;

		handleOnChangeGender();
	}, [dataPriceList, handleOnChangeGender]);

	const handleOnFinish = async (values: FormSalesOrderModal) => {
		// if (status === StatusSalesOrder.Submit && !pathImage.length) {
		// 	return notification.error({
		// 		message: "Vui lòng thêm hình ảnh trước khi xác nhận đơn hàng.",
		// 	});
		// }

		try {
			const { customerId, orderDate, chickenType, shipDate, salesOderDetail, itemCode, farmCode, note, ...rest } = values;

			if (dataSalesOrder?.status === StatusSalesOrder.Submit && !note) {
				return notification.error({
					message: "Vui lòng nhập ghi chú trước khi cập nhật đơn hàng đã xác nhận.",
				});
			}

			const farm = optionsFarms.find((item) => item.farm_code === farmCode);

			if (!farm)
				return notification.error({
					message: "Trại không hợp lệ.",
				});

			const breed = dataBreedInByFarm?.find((category) => category.code === itemCode);

			if (!breed)
				return notification.error({
					message: "Con giống không hợp lệ.",
				});

			const post = {
				customerId,
				customerName: dataGetCustomers?.find((item) => item.customerId === customerId)?.customerName,
				orderDate: dayjs(orderDate).toISOString(),
				shipDate: dayjs(shipDate).toISOString(),
				status,
				pathImage: pathImage.join("|"),
				farmCode,
				note,
				...rest,
			};

			if (!salesOrderId) {
				await createSalesOderV2({
					...post,
					salesOderDetail: [
						{
							itemCode,
							itemName: breed.name,
							chickenType,
							maleQuantity: salesOderDetail.find((item) => item.gender === GenderChicken.Trống)?.quantity || 0,
							femaleQuantity: salesOderDetail.find((item) => item.gender === GenderChicken.Mái)?.quantity || 0,
							noneQuantity: salesOderDetail.find((item) => item.gender === GenderChicken.Xô)?.quantity || 0,
						},
					],
					priceListIds: salesOderDetail.map((item) => item.priceListId),
				}).unwrap();
			} else {
				await updateSalesOderV2({
					...post,
					id: salesOrderId,
				}).unwrap();
			}

			onClose();
			notification.success({
				message: `${!salesOrderId ? "Tạo mới" : "Cập nhập"} đơn hàng thành công !`,
			});
		} catch (error: any) {
			console.log(error);
			notification.error({
				message: error?.data?.message,
			});
		}
	};

	const handleOnSave = () => {
		setStatus(StatusSalesOrder.Draft);
		form.submit();
	};

	const handleOnConfirm = () => {
		setStatus(StatusSalesOrder.Submit);
		form.submit();
	};

	const handleOnChangeCustomerOrFarmOrItemOrChickType = () => {
		const farmId = optionsFarms.find((item) => item.farm_code === form.getFieldValue("farmCode"))?.id;

		const customerCode = form.getFieldValue("customerId");
		const productCode = form.getFieldValue("itemCode");
		const chickenType = form.getFieldValue("chickenType");
		const shipDate = form.getFieldValue("shipDate");

		if (!farmId || !customerCode || !productCode || !chickenType || !shipDate) return;

		getPriceListByCustomerCodeAndProDuctCodeAndFarmId({
			productCode,
			farmId,
			customerCode,
			chickenType,
			status: EnumStatusPriceList.valid,
			validFrom: shipDate.toISOString(),
			validTo: shipDate.toISOString(),
		}).unwrap();
	};

	let isDisabled = false;

	if (dataSalesOrder?.status === StatusSalesOrder.Submit) {
		isDisabled = true;
	}

	return (
		<Drawer
			title={`${!salesOrderId ? "Tạo mới" : "Cập nhập"} đơn hàng`}
			open={open}
			width={1000}
			onClose={onClose}
			footer={
				<Flex gap={10} justify="flex-end">
					<Button disabled={isLoadingQueries || isLoadingMutations || !dataPriceList?.items?.length} onClick={onClose} icon={<CloseOutlined />}>
						Huỷ bỏ
					</Button>
					<Button disabled={isLoadingQueries || isLoadingMutations || !dataPriceList?.items?.length} type="primary" onClick={handleOnSave} ghost icon={<SaveOutlined />}>
						Lưu
					</Button>
					<Button disabled={isLoadingQueries || isLoadingMutations || !dataPriceList?.items?.length} type="primary" onClick={handleOnConfirm} icon={<CheckOutlined />}>
						Xác nhận
					</Button>
				</Flex>
			}>
			<Spin spinning={isLoadingQueries || isLoadingMutations} tip="Đang tải...">
				<Form<FormSalesOrderModal> form={form} onFinish={handleOnFinish} layout="vertical">
					<Row gutter={[40, 0]}>
						<Col xs={24} xl={12}>
							<Form.Item label="Ngày tạo" name="orderDate" initialValue={orderDate} rules={[{ required: true, message: "Chọn ngày tạo" }]}>
								<DatePicker
									disabled
									onChange={(value) => {
										if (!value) return;
										form.setFieldValue("shipDate", value);
										setOrderDate(value);
									}}
									format={"DD-MM-YYYY"}
									disabledDate={(date) => !date || date.isBefore(dayjs().tz("Asia/Ho_Chi_Minh"))}
									style={{ width: "100%" }}
								/>
							</Form.Item>
						</Col>

						<Col xs={24} xl={12}>
							<Form.Item label="Trại" name="farmCode" rules={[{ required: true, message: "Chọn trại" }]}>
								<Select
									placeholder="Chọn"
									showSearch
									allowClear
									// onSearch={handleOnSearchFarms}
									filterOption={handleSelectFilter}
									onClear={handleOnClearFarms}
									notFoundContent={isFetchingGetFarms ? <Spin size="small" /> : <Empty />}
									onChange={(value) => {
										if (!value) return;

										getCategoriesBreedInByFarm({ farmCode: value });

										form.setFieldValue("itemCode", undefined);

										form.setFieldValue(FORM_LIST_NAME, []);

										handleOnChangeCustomerOrFarmOrItemOrChickType();
									}}
									options={optionsFarms?.map((item, key: number) => ({ label: `[${item.farm_code}]: ${item.farm_name}`, value: item.farm_code }))}
								/>
							</Form.Item>
						</Col>

						<Col xs={24} xl={12}>
							<Form.Item label="Khách hàng" name="customerId" rules={[{ required: true, message: "Chọn khách hàng" }]}>
								<Select
									placeholder="Chọn"
									showSearch
									allowClear
									filterOption={handleSelectFilter}
									onChange={(value) => {
										const customer = dataGetCustomers?.find((item) => item.customerId === value);

										if (!customer) return;

										const { customerId, custPhone } = customer;

										form.setFieldValue("custPhone", custPhone);
										form.resetFields(["shipAddress"]);

										getCustomerShipping(customerId);

										handleOnChangeCustomerOrFarmOrItemOrChickType();
									}}
									options={optionsCustomers?.map((item) => ({ label: `[${item.customerId}]: ${item.customerName}`, value: item.customerId }))}
								/>
							</Form.Item>
						</Col>

						<Col xs={24} xl={12}>
							<Form.Item label="SĐT khách hàng" name="custPhone" rules={[{ required: true, message: "Nhập SĐT khách hàng" }]}>
								<Input placeholder="SĐT khách hàng" onKeyPress={onKeyPress} />
							</Form.Item>
						</Col>

						<Col xs={24} xl={12}>
							<Form.Item label="Ngày giao hàng" name="shipDate" initialValue={dayjs().tz("Asia/Ho_Chi_Minh")} rules={[{ required: true, message: "Chọn ngày giao hàng" }]}>
								<DatePicker
									disabled={isDisabled}
									format={"DD-MM-YYYY HH:mm"}
									showTime={{ format: "HH:mm" }}
									disabledDate={(date) => !date || date.isBefore(orderDate)}
									style={{ width: "100%" }}
									onChange={(value) => {
										if (!value) return;

										handleOnChangeCustomerOrFarmOrItemOrChickType();
									}}
								/>
							</Form.Item>
						</Col>

						<Col xs={24} xl={12}>
							<Form.Item label="Địa chỉ giao hàng" name="shipAddress" rules={[{ required: true, message: "Nhập địa chỉ" }]}>
								{dataCustomerShipping?.length ? (
									<Select
										disabled={isDisabled}
										placeholder="Chọn"
										showSearch
										allowClear
										filterOption={handleSelectFilter}
										options={dataCustomerShipping.map((item) => ({
											label: item.cusAddress,
											value: item.cusAddress,
										}))}
									/>
								) : (
									<TextArea placeholder="Địa chỉ giao hàng" rows={1} />
								)}
							</Form.Item>
						</Col>

						<Col xs={24} xl={12}>
							<Form.Item label="Tên tài xế" name="driver">
								<Input placeholder="Tên tài xế" />
							</Form.Item>
						</Col>

						<Col xs={24} xl={12}>
							<Form.Item label="SĐT tài xế" name="driverPhone">
								<Input placeholder="SĐT tài xế" onKeyPress={onKeyPress} />
							</Form.Item>
						</Col>
						<Col xs={24} xl={12}>
							<Form.Item label="Số xe" name="truckId">
								<Input placeholder="Số xe" />
							</Form.Item>
						</Col>
						<Col xs={24} xl={12}>
							<Form.Item label={"Con giống"} name="itemCode" rules={[{ required: true, message: "Chọn con giống" }]}>
								<Select
									style={{ width: "100%" }}
									placeholder="Chọn"
									showSearch
									allowClear
									filterOption={handleSelectFilter}
									options={dataBreedInByFarm?.map((item) => ({
										label: `[${item.code}]: ${item.name}`,
										value: item.code,
									}))}
									onChange={handleOnChangeCustomerOrFarmOrItemOrChickType}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} xl={12}>
							<Form.Item label={"Gà loại"} name="chickenType" rules={[{ required: true, message: "Chọn gà loại" }]}>
								<Select
									style={{ width: "100%" }}
									options={Array(4)
										.fill(99)
										.map((_, index) => ({ label: (index + 1).toString(), value: index + 1 }))}
									allowClear
									placeholder="chọn"
									onChange={handleOnChangeCustomerOrFarmOrItemOrChickType}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} xl={12}>
							<Form.Item label={"Giới tính"} name="gender" initialValue={Object.keys(GenderChicken)} rules={[{ required: true, message: "Chọn giới tính" }]}>
								<Select
									mode="multiple"
									style={{ width: "100%" }}
									options={Object.keys(GenderChicken).map((gender) => ({ label: gender, value: gender }))}
									allowClear
									placeholder="chọn"
									onChange={handleOnChangeGender}
								/>
							</Form.Item>
						</Col>
					</Row>

					<Divider />

					{dataSalesOrder?.status === StatusSalesOrder.Draft ||
						(!dataSalesOrder && (
							<Row>
								<Col span={24}>
									<Skeleton loading={isLoadingQueries || isLoadingMutations}>
										{!dataPriceList?.items.length ? (
											<Alert style={{ marginBottom: 24 }} type="error" message={`Sản phầm chưa có bảng giá, vui lòng thiết lập bảng giá.`} />
										) : (
											<Form.List name={FORM_LIST_NAME}>
												{(fields) => (
													<>
														{fields.map(({ key, name, ...restField }, index) => {
															const salesOderDetail = form.getFieldValue([FORM_LIST_NAME, name]) as FormSalesOrderDetailModal;

															return (
																<div key={key}>
																	<Row gutter={10} key={key}>
																		<Col span={24}>
																			<Typography.Title level={5} style={{ color: COLOR_GENDER[index] }}>
																				Loại gà - {salesOderDetail.gender}
																			</Typography.Title>
																		</Col>
																		{!salesOderDetail.priceListId ? (
																			<Alert type="error" message={`Sản phầm chưa có bảng giá, vui lòng thiết lập bảng giá.`} />
																		) : (
																			<>
																				<Col xs={24} xl={8}>
																					<Form.Item {...restField} name={[name, "priceListId"]} required label={"Mã giá"}>
																						<Select
																							style={{ width: "100%" }}
																							options={dataPriceList?.items.filter((item) => item.gender === salesOderDetail.gender)?.map((item) => ({ label: item.priceListCode, value: item.id }))}
																							allowClear
																							placeholder="chọn"
																							onChange={(id: number) => {
																								if (!id || !dataPriceList) return;

																								const find = dataPriceList?.items.find((item) => item.id === id);
																								form.setFieldValue([FORM_LIST_NAME, index, "priceAmount"], find?.priceAmount);
																								form.setFieldValue([FORM_LIST_NAME, index, "currency"], find?.currency);
																								form.setFieldValue([FORM_LIST_NAME, index, "uom"], find?.uom);
																							}}
																						/>
																					</Form.Item>
																				</Col>
																				<Col xs={24} xl={8}>
																					<Form.Item {...restField} name={[name, "priceAmount"]} required label={"Đơn giá"} initialValue={0}>
																						<InputNumber readOnly style={{ width: "100%", textAlign: "right" }} suffix={salesOderDetail.uom} formatter={(value) => `${salesOderDetail.currency} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")} />
																					</Form.Item>
																				</Col>
																				<Col xs={24} xl={8}>
																					<Form.Item
																						{...restField}
																						name={[name, "quantity"]}
																						required
																						label={"Số lượng"}
																						rules={[
																							{ required: true, message: "Nhập số lượng" },
																							{ type: "number", min: 1, message: "Số lượng tối thiểu là 1" },
																						]}
																						initialValue={0}>
																						<InputNumber style={{ width: "100%" }} placeholder="Số lượng" min={0} controls={false} precision={0} />
																					</Form.Item>
																				</Col>
																			</>
																		)}
																	</Row>
																	{index !== fields.length - 1 && <Divider />}
																</div>
															);
														})}
													</>
												)}
											</Form.List>
										)}
									</Skeleton>
								</Col>
							</Row>
						))}

					<Row>
						<Col span={24}>
							<Form.Item label={"Ghi chú"} name="note">
								<TextArea placeholder="Nhập ghi chú" rows={2} />
							</Form.Item>
						</Col>
					</Row>

					<Row>
						<Col span={24}>
							{!isDisabled ? <AddImage setPathImage={(path: string) => setPathImage([...pathImage, path])} /> : null}
							<Flex gap={20} wrap="wrap" style={{ marginTop: 10 }}>
								{pathImage
									.filter((src) => src)
									.map((src) => (
										<Image style={{ border: "1px solid rgb(239, 239, 239)", borderRadius: 6 }} width={100} height={100} src={getUrlImage(src)} />
									))}
							</Flex>
						</Col>
					</Row>
				</Form>
			</Spin>
		</Drawer>
	);
};

export default SalesOrderModalV2;
